import { Button, Checkbox, CircularProgress, Collapse, IconButton, List, ListItemButton, ListItemText, ListSubheader, Paper, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Tooltip, CircleMarker, useMapEvents, Polyline } from 'react-leaflet';

import { useDispatch, useSelector } from 'react-redux';
import { Add, ExpandLess, ExpandMore, Input } from '@mui/icons-material';
import { useTheme } from '@mui/styles';
import { selectUser } from '../../reducers/userSlice';

import { useSocket } from '../../context/useSocket';
import { setPadData } from '../../reducers/rtfoSlice';
import { useSnackbar } from 'notistack';
import { RTFOCreateNewPad } from './RTFOCreateNewPad';
import { getRTFOClientName } from './rtfoConfig';


const ENV = process.env.REACT_APP_NODE_ENV;
const BASE_URL_SSD = ENV === 'production' ? process.env.REACT_APP_BASE_URL_SSD : (ENV === 'staging' || ENV === 'development' ) ? process.env.REACT_APP_BASE_URL_SSD_STAGING : 'http://localhost:8080/api';


const RTFOMapPadActivity = () => {
  
  const theme = useTheme();

  const { socket } = useSocket();
  const { enqueueSnackbar } = useSnackbar();

  const mapRef = useRef(null);
  const user = useSelector(selectUser);

  const darkMode = user?.value?.darkMode;
  const dispatch = useDispatch();
  
  const [padActivity, setPadActivity] = useState(null);
  const [formations, setFormations] = useState([]);
  const [currentPadName, setCurrentPadName] = useState("");
  const [open, setOpen] = useState([]);
  const [checked, setChecked] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(9);
  const [createNewPadOpen, setCreateNewPadOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [newPad, setNewPad] = useState(null);

  const isLoading = useRef(true);

  if (socket) {
    socket.onmessage = (event) => {
      const response = JSON.parse(event?.data);
  
      if (!response) return;
  
      if (response.messageType === 'padData') {
        const padData = response?.payload;
  
        if (padData) {
          
          if (padData.stages?.length > 0) {
            dispatch(setPadData({...padData, isLoadingData: true }));
            
            enqueueSnackbar(`Downloading pad ${padData?.name} with ${padData?.stages?.length || 0} stages...`, {
              variant: 'info',
              persist: true
            });
          } else {
            dispatch(setPadData(padData));
          }
        };      
      }
    };
  };

  useEffect(() => {
    const fetchData = async () => {

      try {
        const result = await fetch(`${BASE_URL_SSD}/rtfo/activity`, {
          method: 'GET',
          headers: {
            'x-api-key': process.env.REACT_APP_API_KEY,
            client: getRTFOClientName(user?.value?.email)
          }
        });
        
        const activity = await result.json();

        activity.sort((a, b) => new Date(b?.createdOn) - new Date(a?.createdOn))
        setPadActivity(activity);
        const newFormations = [...new Set(activity?.map(p => `${p.formation} - ${p.area}`))]; 

        setFormations(newFormations);
        setOpen(newFormations);
        const allPads = activity?.map(p => p.name);
        setChecked(allPads);

        isLoading.current = false;
      } catch (error) {
        console.log(error);
        
        isLoading.current = false;
      };

    };

    if (!padActivity) {
      fetchData();
    };

  }, [padActivity, user?.value?.email]);


  // Calculate pad coordinates
  const padCoordinates = useMemo(() => {
    const coords = {};
    if (!padActivity) return [];

    for (let pad of padActivity) {

      const wellCoords = {};

      const wells = Object.keys(pad?.surveys?.wells || {});

      for (let i = 0; i < wells.length; i++) { // calculate well paths
        const lat = pad?.surveys?.wells?.[wells[i]]?.surfaceLat;
        const lon = pad?.surveys?.wells?.[wells[i]]?.surfaceLon;
        const surveyData = pad?.surveys?.wells?.[wells[i]]?.surveyData;
        const wellLatLngs = surveyToCoordinates(lat, lon, surveyData);

        if (!wellCoords[wells[i]]) wellCoords[wells[i]] = [];

        wellCoords[wells[i]] = wellLatLngs;
      };

      const wellCoordinatesArray = Object.values(wellCoords);
      const center = getMiddlePoint(wellCoordinatesArray);
      coords[`${pad.name}`] = center;
    };

    return coords;
  }, [padActivity]);

  // Calculate well coordinates
  const wellCoordinates = useMemo(() => {
    const coords = {};
    if (!padActivity) return [];

    let sortedPadActivity = [...padActivity];

    // move recent pads to the top
    sortedPadActivity.sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));

    // move the selected pad to the top
    if (showDetails) {
      sortedPadActivity = sortedPadActivity.filter(a => a.name !== showDetails);
      sortedPadActivity.push(padActivity.find(a => a.name === showDetails));
    };

    for (let pad of sortedPadActivity) {

      const wellCoords = {};

      const wells = Object.keys(pad?.surveys?.wells || {});

      for (let i = 0; i < wells.length; i++) { // calculate well paths
        const lat = pad?.surveys?.wells?.[wells[i]]?.surfaceLat;
        const lon = pad?.surveys?.wells?.[wells[i]]?.surfaceLon;
        const surveyData = pad?.surveys?.wells?.[wells[i]]?.surveyData;
        const wellLatLngs = surveyToCoordinates(lat, lon, surveyData);

        if (!wellCoords[wells[i]]) wellCoords[wells[i]] = [];

        wellCoords[wells[i]] = wellLatLngs;
      };

      coords[`${pad.name}`] = wellCoords;
    };

    return coords;
  }, [padActivity, showDetails]);

  const globalCenter = useMemo(() => {
    
    if (!padActivity) return [];

    const allPads = Object.keys(wellCoordinates);

    const lines = [];

    for (let pad of allPads) {
      const wells = Object.keys(wellCoordinates[pad]);

      for (let well of wells) {
        const wellCoords = wellCoordinates[pad][well];
        if (wellCoords.length > 0) {
          lines.push(wellCoords);
        };
      };
    };

    let center = getMiddlePoint(lines);
    if (center?.[0] === 0 || center?.[1] === 0) {
      center = [55.000000, -115.000000];
    };

    return center;
  }, [padActivity, wellCoordinates]);
  
  useEffect(() => { // pan to pad when pad name changes
    if (currentPadName?.length > 0 && mapRef?.current) {
      mapRef?.current?.flyTo(padCoordinates[currentPadName], 14, {animate: true, duration: 1});
    };
  }, [currentPadName, padCoordinates]);


  if (globalCenter?.length > 0 && !isLoading.current) {
    return (
      <Paper style={{height: 'calc(100%-20px)', width: '100%', marginTop: 20, backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')], position: 'relative'}} elevation={0} sx={{boxShadow: 1}}>        
        
        <div style={{width: 300, maxHeight: 'calc(100% - 40px)', backgroundColor: '#fff', borderRadius: '5px', boxShadow: '1px 1px 5px grey', position: 'absolute', top: 20, left: 20, zIndex: 420, overflowY: 'auto'}}>
          <List
            subheader={
              <ListSubheader component="div">
                Pad Activity
              </ListSubheader>
            }
          >
            <div style={{width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 20}}>
              <Button startIcon={<Add />} variant="outlined" size="small" style={{height: 28}} onClick={() => setCreateNewPadOpen(true)} disabled={!user?.value?.email?.includes('@ssdynamics.com')}>
                Create New Pad
              </Button>
            </div>

            {formations?.map(formation => {
              return (
                <div key={formation}>
                  <ListItemButton 
                    onClick={() => handleOpen(formation)} 
                  >
                    <ListItemText primary={<Typography fontSize="12px">{user?.value?.email === 'demo@ssdynamics.com' ? 'Formation - Area' : formation}</Typography>} />
                    {open?.includes(formation) ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={open?.includes(formation)} timeout="auto" unmountOnExit>
                    <List component="div">
                      {padActivity?.filter(p => `${p.formation} - ${p.area}` === formation)?.map(pad => {
                        return (
                          <ListItemButton 
                            key={pad?.name} 
                            selected={currentPadName === pad?.name} 
                            style={{width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 36, borderBottom: '1px solid lightgrey'}} 
                            onClick={() => {
                              setCurrentPadName(pad?.name);
                              setShowDetails(pad?.name);
                            }}
                          > 
                            <IconButton
                              disableRipple
                              style={{width: 16, height: 16, marginRight: 20}}
                              onClick={(e) => handleCheck(e, pad?.name)}
                            >
                              <Checkbox
                                checked={checked.indexOf(pad?.name) !== -1}
                              />
                            </IconButton>
                            <Typography fontSize='12px' style={{width: 75, marginRight: 10}}>{pad?.name}</Typography>
                            <div style={{width: 12, height: 12, borderRadius: '50%', backgroundColor: pad?.isActive ? 'green' : 'red', margin: '0 10px'}}></div>
                            <Typography fontSize='12px' style={{width: 75}} >{pad?.isActive ? 'Active' : 'Completed'}</Typography>
                            <IconButton 
                              title="Load Pad Data"
                              size="small"
                              onClick={() => handleLoadPadDataClick(pad?.name)}
                            >
                              <Input fontSize='16px'/>
                            </IconButton>
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                </div>
              )
            })}
            
          </List>
        </div>

        <MapContainer center={globalCenter} zoom={zoomLevel} maxZoom={16} ref={mapRef} style={{height: '100%', width: '100%'}} attributionControl={false} zoomControl={false}>
          <MyComponent />

          {darkMode ? 
            <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}`} removeAttribution attributionControl="false"  /> :
            <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}`} removeAttribution attributionControl="false"  />}
          
          {/* <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile/{z}/{y}/{x}`} removeAttribution attributionControl="false"  /> */}
          {/* <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/World_Shaded_Relief/MapServer/tile/{z}/{y}/{x}`} removeAttribution attributionControl="false"  /> */}
          {/* <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}`} removeAttribution attributionControl="false"  /> */}
          {/* <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}`} removeAttribution attributionControl="false"  /> */}
          {/* <TileLayer url={`https://tile.openstreetmap.org/{z}/{x}/{y}.png`} removeAttribution attributionControl="false" /> */}
        
          {Object.keys(wellCoordinates)?.filter(p => checked.indexOf(p) !== -1)?.sort((a, b) => b.isActive - a.isActive)?.map(pad => {
            return (
              Object.keys(wellCoordinates?.[pad])?.map((well, j) => {
                
                const padData = padActivity?.find(p => p.name === pad);

                return (
                  <div key={`${pad}-${well}`}>
                    <Polyline pathOptions={{ color: currentPadName === pad ? 'red' : 'black', weight: 4 }} positions={wellCoordinates?.[pad]?.[well]} bounds={L.latLngBounds(wellCoordinates?.[pad]?.[well])}/>
                    <CircleMarker 
                      center={[padActivity?.find(p => p.name === pad)?.surveys?.wells?.[well]?.surfaceLat, padActivity?.find(p => p.name === pad)?.surveys?.wells?.[well]?.surfaceLon]} 
                      pathOptions={{ color: currentPadName === pad ? 'red' : 'black' }} 
                      radius={10}
                      // eventHandlers={{
                      //   click: (e) => {
                      //     setShowDetails(prev => prev === pad ? false : pad);
                      //   }
                      // }}
                    >
                      {j === 0 && <Tooltip direction={"bottom"} offset={[0, 0]} opacity={1} permanent interactive={true}>
                        <div style={{display: 'flex', flexDirection: 'column'}} onClick={() => {
                          setShowDetails(prev => prev === pad ? false : pad);
                        }}>
                          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}} >
                            <Typography fontSize="14px" textAlign={'center'}>{pad}</Typography>
                            <div style={{width: 12, height: 12, borderRadius: '50%', backgroundColor: padData?.isActive ? 'green' : 'red', margin: '0 5px'}}></div>
                          </div>
                          {showDetails === pad && 
                            <div style={{display: 'flex', flexDirection: 'column'}}>
                              <Typography variant='subtitle2'>Wells: {Object.keys(padData?.surveys?.wells)?.length || 0}</Typography>
                              <Typography variant='subtitle2'>Stages: {padData?.stages?.length || 0}</Typography>
                              <Typography variant='subtitle2'>Status: {padData?.isActive ? 'Active' : 'Completed'}</Typography>
                              <Typography variant='subtitle2'>Created: {new Date(padData?.createdOn)?.toLocaleString()}</Typography>
                              <Typography variant='subtitle2'>Modified: {new Date(padData?.lastModified)?.toLocaleString()}</Typography>
                              <Typography variant='subtitle2' textAlign={'center'}>UWIs</Typography>
                              <div style={{display: 'flex', flexDirection: 'column'}}>
                                {(!padData?.wellMapping || padData?.wellMapping?.length === 0) ? [] : Object.values(padData?.wellMapping)?.map((w,i) => (
                                  <Typography textAlign={'center'} fontSize={13} key={w + '-' + i}>{w}</Typography>
                                ))}
                              </div>
                              <div style={{display: 'flex', justifyContent: 'center', width: '100%', marginTop: 5}}>
                                <Button onClick={() => handleLoadPadDataClick(pad)} size="small" style={{height: 28}} startIcon={<Input fontSize='16px'/>}><Typography variant='subtitle2'>Load Pad Data</Typography></Button>
                              </div>
                            </div>
                          }
                        </div>
                      </Tooltip>}
                    </CircleMarker>
                  </div>
                )
              })
            )
            
          })}
          

        </MapContainer>

        {createNewPadOpen && <RTFOCreateNewPad 
          newPad={newPad}
          setNewPad={setNewPad}
          setCreateNewPadOpen={setCreateNewPadOpen}
          padActivity={padActivity}
          setPadActivity={setPadActivity}
          setFormations={setFormations}
          setOpen={setOpen}
          setChecked={setChecked}
          darkMode={darkMode}
          userEmail={user?.value?.email}
        />}
      </Paper>
    );
  }
  else {
    return (
      <div style={{width: '100vw', height: '100vh', position: 'relative'}}>
        <LoadingRTFOData />
      </div>
    );
  };


  function handleLoadPadDataClick(padName) {
    socket?.readyState === 1 &&
    socket?.send(JSON.stringify({
      messageType: 'getPadData',
      padName: padName,
      initialLoad: true,
      getFullData: user?.value?.email === 'bhepburn@wcap.ca' ? true : false,
      userEmail: user?.value?.email
    }));
  };


  function LoadingRTFOData() {
    return (
      <div style={{position: 'absolute', zIndex: 666, width: '100%', height: '100%', backgroundColor: 'rgba(255,255,255,0.8)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
        <CircularProgress />
        <Typography style={{marginTop: 20}}>Loading pad activity...</Typography>
      </div>
    )
  };

  function MyComponent() { // This component is used to get the zoom level of the map
   
    const mapEvents = useMapEvents({
        zoomend: () => {
            setZoomLevel(mapEvents.getZoom());
        },
    });

    return null;
  };

  function handleOpen(formation) {
    if (open?.includes(formation)) {
      setOpen(open?.filter(f => f !== formation));
    } else {
      setOpen([...open, formation]);
    };
  };

  function handleCheck(e, pad) {
    e.stopPropagation();
    if (checked?.includes(pad)) {
      setChecked(checked?.filter(p => p !== pad));
    } else {
      setChecked([...checked, pad]);
    };
  };
};

export default RTFOMapPadActivity;

function surveyToCoordinates(surfaceLat, surfaceLon, surveyData) {
  try {
    let latLngs = [];
  
    // Initialize variables for minimum curvature algorithm
    let x0 = surfaceLat;
    let y0 = surfaceLon;
    let z0 = 0;
    let mdPrev = 0;
  
    // Iterate through survey points to calculate wellbore path
    for (let i = 0; i < surveyData.length; i++) {
      let md = surveyData[i].measuredDepth;
      let dip = surveyData[i].deviation;
      let azi = surveyData[i].direction;
      let dMD = md - mdPrev;
      let radians = dip * Math.PI / 180;
      let aziRad = azi * Math.PI / 180;
  
      // Calculate new coordinates with minimum curvature algorithm
      let dx = dMD * Math.sin(radians) * Math.cos(aziRad);
      let dy = dMD * Math.sin(radians) * Math.sin(aziRad);
      let dz = dMD * Math.cos(radians);
      let x = x0 + dx / 111111;
      let y = y0 + dy / (111111 * Math.cos(surfaceLat * Math.PI / 180));
      let z = z0 + dz;
      let lat = x;
      let lon = y;
  
      // Add the lat, lon coordinates to the array
      latLngs.push([lat, lon]);
  
      // Update variables for next iteration
      x0 = x;
      y0 = y;
      z0 = z;
      mdPrev = md;
  
    };
  
    return latLngs;
    
  } catch (error) {
    console.log(error);
    return [];
  };
};

function getMiddlePoint(lines) {
  try {
    let totalLat = 0;
    let totalLon = 0;
    let totalPoints = 0;

    for (const line of lines) {
      let lineLat = 0;
      let lineLon = 0;
      const numPoints = line?.length;
  
      for (const point of line) {
        lineLat += point[0];
        lineLon += point[1];
      };
  
      totalLat += lineLat / numPoints;
      totalLon += lineLon / numPoints;
      totalPoints++;
    };
  
    const avgLat = totalLat / totalPoints;
    const avgLon = totalLon / totalPoints;
  
    if (isNaN(avgLat) || isNaN(avgLon)) {
      return [0, 0];
    };

    return [avgLat, avgLon];
    
  } catch (error) {
    console.log(error);
    return [0, 0];
  };
};
